import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import ReactFlow, {
    Background,
    Controls,
    MiniMap,
    addEdge,
    Panel,
    applyNodeChanges,
    applyEdgeChanges
} from 'reactflow';
import {
    selectNodes,
    selectEdges,
    selectSelectedNode,
    selectDrawerVisible,
    selectPaletteCollapsed,
    selectCurrentRule,
    selectSaving,
    selectNodeIdCounter,
    setNodes,
    setEdges,
    loadFlow,
    addNode,
    updateNode,
    deleteNode,
    addEdge as addEdgeAction,
    updateEdges,
    clearFlow,
    setSelectedNode,
    setDrawerVisible,
    setPaletteCollapsed,
    setCurrentRule,
    setSaving,
    saveToHistory
} from '../../../../redux/slices/adsOptimizationSlice';
import { Button, Space, message, Typography, Modal, Form, Input, Drawer, Dropdown, Tooltip, Select, Tag, Divider } from 'antd';
import { 
    PlayCircleOutlined, 
    SaveOutlined, 
    ClearOutlined, 
    LeftOutlined, 
    DatabaseOutlined,
    MenuOutlined,
    DragOutlined,
    SelectOutlined,
    DeleteOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CloseOutlined,
    FileTextOutlined,
    BugOutlined,
    AimOutlined,
    StepForwardOutlined,
    StopOutlined,
    PauseCircleOutlined,
    CopyOutlined,
    SnippetsOutlined,
    ScissorOutlined,
    ClockCircleOutlined,
    CalendarOutlined,
    ThunderboltOutlined,
    PlusOutlined
} from '@ant-design/icons';

import CustomNode from './CustomNode';
import DecorationNode from './DecorationNode';
import AnimatedEdge from './AnimatedEdge';
import NodePalette from './NodePalette';
import PropertyDrawer from './PropertyDrawer';
import InputModal from './InputModal';
import ExecutionResultsPanel from './ExecutionResultsPanel';
// import GlobalVariablesPanel from './GlobalVariablesPanel';
import globalVariableService from '../../services/GlobalVariableService';
import WorkflowAPIService from '../../services/WorkflowAPIService';
import { usePerformanceMonitor, MemoizedCustomNode } from './PerformanceOptimizations';
import useSchemaPropagation from '../../hooks/useSchemaPropagation';
import { NODE_DEFINITIONS } from '../../utils/nodeTypes';

import 'reactflow/dist/style.css';

const { Text } = Typography;

/**
 * Helper: Update batch_start input labels based on connected edges
 * Only updates labels for existing inputs, does NOT add/remove inputs
 * Returns updated nodes array if changes needed, null otherwise
 */
const updateBatchStartInputLabels = (nodes, edges, batchStartNodeId) => {
    const batchStartNode = nodes.find(n => n.id === batchStartNodeId);
    if (!batchStartNode) return null;
    
    const nodeType = batchStartNode.data?.nodeType || batchStartNode.data?.type;
    if (nodeType !== 'batch_start') return null;
    
    // Get current inputs from config
    const currentInputs = batchStartNode.data?.config?.dynamicInputs || [{ id: 'input_1', name: 'input_1' }];
    
    // Get all edges targeting this batch_start
    const incomingEdges = edges.filter(e => e.target === batchStartNodeId);
    
    // Update labels for inputs that have connected edges
    const updatedInputs = currentInputs.map(input => {
        // Find edge connected to this input handle
        const connectedEdge = incomingEdges.find(e => e.targetHandle === input.id);
        if (connectedEdge) {
            // Get name from source node
            const sourceNode = nodes.find(n => n.id === connectedEdge.source);
            const sourceName = sourceNode?.data?.config?.datasetName ||
                              sourceNode?.data?.config?.name ||
                              sourceNode?.data?.label;
            if (sourceName && sourceName !== input.name) {
                return { ...input, name: sourceName, sourceNodeId: connectedEdge.source };
            }
        }
        return input;
    });
    
    // Check if anything changed
    const changed = JSON.stringify(updatedInputs) !== JSON.stringify(currentInputs);
    if (!changed) return null;
    
    // Update node config
    const updatedNodes = nodes.map(n => {
        if (n.id === batchStartNodeId) {
            return {
                ...n,
                data: {
                    ...n.data,
                    config: {
                        ...n.data.config,
                        dynamicInputs: updatedInputs
                    }
                }
            };
        }
        return n;
    });
    
    return updatedNodes;
};

/**
 * Helper: Find paired batch_end for a batch_start and sync outputs
 * Returns updated nodes array if changes needed, null otherwise
 */
const syncBatchEndOutputs = (nodes, edges, batchStartNodeId) => {
    const batchStartNode = nodes.find(n => n.id === batchStartNodeId);
    if (!batchStartNode) return null;
    
    const nodeType = batchStartNode.data?.nodeType || batchStartNode.data?.type;
    if (nodeType !== 'batch_start') return null;
    
    // BFS to find paired batch_end (traverse forward from batch_start)
    const visited = new Set();
    const queue = [batchStartNodeId];
    let batchEndNode = null;
    
    while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        
        const node = nodes.find(n => n.id === currentId);
        const nType = node?.data?.nodeType || node?.data?.type;
        
        if (nType === 'batch_end' && currentId !== batchStartNodeId) {
            batchEndNode = node;
            break;
        }
        
        // Add downstream nodes
        edges.filter(e => e.source === currentId).forEach(e => {
            queue.push(e.target);
        });
    }
    
    if (!batchEndNode) return null;
    
    // Get inputs from batch_start
    const batchStartConfig = batchStartNode.data?.config || {};
    const dynamicInputs = batchStartConfig.dynamicInputs || [];
    
    // Create matching outputs for batch_end
    const dynamicOutputs = dynamicInputs.map((input, idx) => ({
        id: `output_${idx + 1}`,
        name: input.name || `output_${idx + 1}`
    }));
    
    // If no outputs, keep at least one default
    if (dynamicOutputs.length === 0) {
        dynamicOutputs.push({ id: 'output_1', name: 'output_1' });
    }
    
    // Update batch_end node config
    const updatedNodes = nodes.map(n => {
        if (n.id === batchEndNode.id) {
            return {
                ...n,
                data: {
                    ...n.data,
                    config: {
                        ...n.data.config,
                        dynamicOutputs,
                        pairedBatchStartId: batchStartNodeId
                    }
                }
            };
        }
        return n;
    });
    
    return updatedNodes;
};

/**
 * Helper: Find batch_start for a batch_end by traversing backwards
 * Returns the batch_start node id if found
 */
const findBatchStartForBatchEnd = (nodes, edges, batchEndNodeId) => {
    const batchEndNode = nodes.find(n => n.id === batchEndNodeId);
    if (!batchEndNode) return null;
    
    const nodeType = batchEndNode.data?.nodeType || batchEndNode.data?.type;
    if (nodeType !== 'batch_end') return null;
    
    // BFS backwards to find batch_start
    const visited = new Set();
    const queue = [batchEndNodeId];
    
    while (queue.length > 0) {
        const currentId = queue.shift();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        
        const node = nodes.find(n => n.id === currentId);
        const nType = node?.data?.nodeType || node?.data?.type;
        
        if (nType === 'batch_start' && currentId !== batchEndNodeId) {
            return currentId; // Found batch_start
        }
        
        // Add upstream nodes (traverse backwards via edges where current is target)
        edges.filter(e => e.target === currentId).forEach(e => {
            queue.push(e.source);
        });
    }
    
    return null;
};

const FlowCanvas = React.memo(({ 
    currentRule = null, 
    ruleInfo = null, 
    onBackToRules, 
    onEditRule,
    onFlowChange,
    onExecuteWorkflow,
    onDebugToNode,
    onDebugNextNode,
    onStopDebug,
    isExecuting = false,
    executionResult = null,
    showExecutionPanel = false,
    onShowExecutionResults,
    onCloseExecutionResults,
    executingNodeId = null,
    completedNodeIds = [],
    errorNodeId = null,
    animatingEdgeIds = [],
    debugNodeId = null,
    debugPausedAtNodeId = null,
    isDebugMode = false,
    onSetDebugNode,
    onClearDebugNode,
    canUpdateWorkflow = false,
    canExecuteWorkflow = false
}) => {
    const dispatch = useDispatch();
    
    // Convert completedNodeIds array to Set for O(1) lookup (major performance improvement)
    const completedNodeIdsSet = useMemo(() => new Set(completedNodeIds), [completedNodeIds]);
    
    // Redux selectors
    const nodes = useSelector(selectNodes);
    const edges = useSelector(selectEdges);
    const selectedNode = useSelector(selectSelectedNode);
    const drawerVisible = useSelector(selectDrawerVisible);
    const paletteCollapsed = useSelector(selectPaletteCollapsed);
    const saving = useSelector(selectSaving);
    // const nodeIdCounter = useSelector(selectNodeIdCounter); // Removed as ID generation is handled in Redux
    
    // Performance monitoring
    usePerformanceMonitor();

    // Schema Propagation (Smart UI)
    useSchemaPropagation();
    
    // Local state for modals và UI components không cần global
    const [inputModal, setInputModal] = useState({ visible: false, nodeData: null });
    const [variablesPanel, setVariablesPanel] = useState(false);
    const [variablesData, setVariablesData] = useState({});
    const [newVarName, setNewVarName] = useState('');
    const [newVarValue, setNewVarValue] = useState('');
    const [rfInstance, setRfInstance] = useState(null);
    const lastClickPos = useRef(null);
    const [selectionMode, setSelectionMode] = useState(false); // false: pan (hand), true: select (box)
    const [selectedEdge, setSelectedEdge] = useState(null);
    const [localShowExecutionPanel, setLocalShowExecutionPanel] = useState(false);
    
    // Canvas marker state - shows a red dot where user clicked to indicate node creation position
    // Stores both screen position (for display) and flow position (for node creation)
    const [canvasMarker, setCanvasMarker] = useState(null); // { screenX, screenY, flowX, flowY } or null
    const canvasContainerRef = useRef(null);
    
    // Clipboard state for copy/paste functionality
    const [copiedNodes, setCopiedNodes] = useState([]);
    const [copiedEdges, setCopiedEdges] = useState([]);
    const [selectedNodes, setSelectedNodes] = useState([]); // Multi-selection tracking
    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, nodeId: null });
    const nodeIdCounterRef = useRef(0); // Local counter for generating unique IDs
    
    // Local nodes state for smooth dragging - only sync to Redux on drag end
    const [localNodes, setLocalNodes] = useState(null); // null means use Redux nodes
    const isDraggingRef = useRef(false);
    
    // Track previous nodes reference to detect Redux updates
    const prevNodesRef = useRef(nodes);

    // Sync local state with props
    useEffect(() => {
        setLocalShowExecutionPanel(showExecutionPanel);
    }, [showExecutionPanel]);
    
    // Clear localNodes when Redux nodes change (but not due to dragging)
    // This ensures that node config updates from PropertyDrawer are reflected
    useEffect(() => {
        // If we're not dragging and Redux nodes have changed, clear localNodes
        if (!isDraggingRef.current && localNodes && prevNodesRef.current !== nodes) {
            console.log('FlowCanvas: Redux nodes changed, clearing localNodes to sync');
            setLocalNodes(null);
        }
        prevNodesRef.current = nodes;
    }, [nodes, localNodes]);

    // Handle close execution panel
    const handleCloseExecutionPanel = useCallback(() => {
        console.log('handleCloseExecutionPanel called');
        setLocalShowExecutionPanel(false);
        if (onCloseExecutionResults) {
            onCloseExecutionResults();
        }
    }, [onCloseExecutionResults]);

    // Handle show execution panel
    const handleShowExecutionPanel = useCallback(() => {
        console.log('handleShowExecutionPanel called');
        setLocalShowExecutionPanel(true);
        if (onShowExecutionResults) {
            onShowExecutionResults();
        }
    }, [onShowExecutionResults]);
    
    // Core node update function - will be throttled for position changes
    const updateNodesInRedux = useCallback((updatedNodes) => {
        dispatch(setNodes(updatedNodes));
    }, [dispatch]);
    
    // Get the actual nodes to use (local during drag, Redux otherwise)
    const activeNodes = localNodes || nodes;
    
    // ReactFlow callbacks for nodes and edges - optimized for smooth dragging
    const onNodesChange = useCallback((changes) => {
        // Check if this is only position changes (dragging)
        const isOnlyPositionChange = changes.every(c => c.type === 'position');
        
        if (isOnlyPositionChange) {
            // During drag: update local state only (no Redux dispatch = no lag)
            isDraggingRef.current = true;
            const baseNodes = localNodes || nodes;
            const updatedNodes = applyNodeChanges(changes, baseNodes);
            setLocalNodes(updatedNodes);
        } else {
            // For non-position changes: apply to Redux immediately
            const baseNodes = localNodes || nodes;
            const updatedNodes = applyNodeChanges(changes, baseNodes);
            
            // Check for dimension changes (from NodeResizer) and update config
            changes.forEach(change => {
                if (change.type === 'dimensions' && change.dimensions) {
                    const node = updatedNodes.find(n => n.id === change.id);
                    if (node && node.data?.nodeType === 'shape_rectangle') {
                        const nodeIndex = updatedNodes.findIndex(n => n.id === change.id);
                        if (nodeIndex !== -1) {
                            updatedNodes[nodeIndex] = {
                                ...updatedNodes[nodeIndex],
                                data: {
                                    ...updatedNodes[nodeIndex].data,
                                    config: {
                                        ...updatedNodes[nodeIndex].data.config,
                                        width: Math.round(change.dimensions.width),
                                        height: Math.round(change.dimensions.height)
                                    }
                                }
                            };
                        }
                    }
                }
            });
            
            // Sync local state if exists, then update Redux
            if (localNodes) {
                setLocalNodes(null);
            }
            dispatch(setNodes(updatedNodes));
        }
    }, [dispatch, nodes, localNodes]);
    
    // Sync to Redux when drag ends
    const onNodeDragStop = useCallback((event, node, draggedNodes) => {
        if (isDraggingRef.current && localNodes) {
            isDraggingRef.current = false;
            dispatch(setNodes(localNodes));
            setLocalNodes(null);
        }
    }, [dispatch, localNodes]);
    
    const onEdgesChange = useCallback((changes) => {
        const updatedEdges = applyEdgeChanges(changes, edges);
        dispatch(setEdges(updatedEdges));
        
        // Check if selected edge was removed via keyboard
        if (selectedEdge) {
            const removed = changes.some(c => c.type === 'remove' && c.id === selectedEdge.id);
            if (removed) {
                setSelectedEdge(null);
            }
        }
        
        // Handle batch node sync when edges are removed
        const removedEdges = changes.filter(c => c.type === 'remove');
        if (removedEdges.length > 0) {
            // Find all batch_start nodes that might be affected
            const affectedBatchStarts = new Set();
            
            removedEdges.forEach(change => {
                const removedEdge = edges.find(e => e.id === change.id);
                if (removedEdge) {
                    const targetNode = nodes.find(n => n.id === removedEdge.target);
                    const targetNodeType = targetNode?.data?.nodeType || targetNode?.data?.type;
                    if (targetNodeType === 'batch_start') {
                        affectedBatchStarts.add(removedEdge.target);
                    }
                }
            });
            
            // Update affected batch_start nodes
            if (affectedBatchStarts.size > 0) {
                setTimeout(() => {
                    let currentNodes = nodes;
                    affectedBatchStarts.forEach(batchStartId => {
                        const updated = updateBatchStartInputLabels(currentNodes, updatedEdges, batchStartId);
                        if (updated) {
                            currentNodes = updated;
                            const synced = syncBatchEndOutputs(currentNodes, updatedEdges, batchStartId);
                            if (synced) currentNodes = synced;
                        }
                    });
                    if (currentNodes !== nodes) {
                        dispatch(setNodes(currentNodes));
                    }
                }, 0);
            }
        }
    }, [dispatch, edges, selectedEdge, nodes]);

    // Handle selection change - track both edges and multi-selected nodes
    const onSelectionChange = useCallback(({ nodes: selNodes, edges: selectedEdges }) => {
        // Track multi-selected nodes
        setSelectedNodes(selNodes);
        
        if (selectedEdges.length > 0) {
            setSelectedEdge(selectedEdges[0]);
        } else {
            setSelectedEdge(null);
        }
    }, []);

    // Generate unique node ID
    const generateNodeId = useCallback(() => {
        const timestamp = Date.now();
        const counter = nodeIdCounterRef.current++;
        return `node_${timestamp}_${counter}`;
    }, []);

    // Copy selected nodes to clipboard
    const handleCopyNodes = useCallback(() => {
        const nodesToCopy = selectedNodes.length > 0 
            ? selectedNodes 
            : (selectedNode ? [selectedNode] : []);
        
        if (nodesToCopy.length === 0) {
            message.warning('Please select nodes to copy');
            return;
        }
        
        // Deep clone nodes data
        const clonedNodes = nodesToCopy.map(node => ({
            ...node,
            data: { ...node.data, config: { ...node.data?.config } }
        }));
        
        // Find edges between selected nodes
        const nodeIds = new Set(nodesToCopy.map(n => n.id));
        const relatedEdges = edges.filter(edge => 
            nodeIds.has(edge.source) && nodeIds.has(edge.target)
        );
        
        setCopiedNodes(clonedNodes);
        setCopiedEdges(relatedEdges);
        
        message.success(`Copied ${clonedNodes.length} node(s)`);
    }, [selectedNodes, selectedNode, edges]);

    // Paste nodes from clipboard
    const handlePasteNodes = useCallback(() => {
        if (copiedNodes.length === 0) {
            message.warning('Nothing to paste. Copy nodes first.');
            return;
        }
        
        // Calculate paste position based on last click position (like adding new node)
        let pastePosition;
        
        if (lastClickPos.current) {
            // Use last click position
            pastePosition = lastClickPos.current;
        } else if (rfInstance && rfInstance.screenToFlowPosition) {
            // Center of the view
            const paletteWidth = paletteCollapsed ? 0 : 300;
            const centerX = paletteWidth + (window.innerWidth - paletteWidth) / 2;
            pastePosition = rfInstance.screenToFlowPosition({
                x: centerX,
                y: window.innerHeight / 2
            });
        } else {
            // Fallback - use offset from original position
            pastePosition = null;
        }
        
        // Calculate the bounding box of copied nodes to determine offset
        let offsetX = 50, offsetY = 50; // Default offset if no click position
        
        if (pastePosition) {
            // Find the top-left corner of copied nodes group
            const minX = Math.min(...copiedNodes.map(n => n.position.x));
            const minY = Math.min(...copiedNodes.map(n => n.position.y));
            
            // Calculate offset to move the group so top-left aligns with paste position
            offsetX = pastePosition.x - minX;
            offsetY = pastePosition.y - minY;
        }
        
        // Map old IDs to new IDs
        const idMapping = {};
        
        // Create new nodes with new IDs and calculated positions
        // Mark new nodes as selected
        const newNodes = copiedNodes.map(node => {
            const newId = generateNodeId();
            idMapping[node.id] = newId;
            
            const nodeType = node.data?.nodeType || node.data?.type;
            const nodeDef = NODE_DEFINITIONS[nodeType];
            const isDecoration = nodeDef?.isDecoration === true;
            const zIndex = nodeType === 'shape_rectangle' ? -10 : (isDecoration ? 1 : 10);
            
            return {
                ...node,
                id: newId,
                position: {
                    x: node.position.x + offsetX,
                    y: node.position.y + offsetY
                },
                type: isDecoration ? 'decoration' : 'custom',
                zIndex,
                selected: true, // Mark new nodes as selected
                data: {
                    ...node.data,
                    config: { ...node.data?.config }
                }
            };
        });
        
        // Create new edges with updated source/target IDs
        const newEdges = copiedEdges.map(edge => ({
            ...edge,
            id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            source: idMapping[edge.source],
            target: idMapping[edge.target],
            zIndex: edge.zIndex ?? 5 // Ensure edges appear above rectangles
        }));
        
        // Deselect all existing nodes first, then add new selected nodes
        const deselectedExistingNodes = nodes.map(node => ({
            ...node,
            selected: false
        }));
        dispatch(setNodes([...deselectedExistingNodes, ...newNodes]));
        
        // Add new edges
        newEdges.forEach(edge => dispatch(addEdgeAction(edge)));
        dispatch(saveToHistory());
        
        // Update local selectedNodes state with newly pasted nodes
        setSelectedNodes(newNodes);
        
        // Clear single selected node since we now have multi-selection
        dispatch(setSelectedNode(null));
        dispatch(setDrawerVisible(false));
        
        // Clear the canvas marker after pasting nodes
        setCanvasMarker(null);
        lastClickPos.current = null;
        
        // Keep original copied nodes for next paste (don't update positions)
        // This allows pasting at different locations with consistent behavior
        
        message.success(`Pasted ${newNodes.length} node(s)`);
    }, [copiedNodes, copiedEdges, nodes, generateNodeId, dispatch, rfInstance, paletteCollapsed]);

    // Duplicate selected nodes (copy + paste in one action)
    const handleDuplicateNodes = useCallback(() => {
        const nodesToDuplicate = selectedNodes.length > 0 
            ? selectedNodes 
            : (selectedNode ? [selectedNode] : []);
        
        if (nodesToDuplicate.length === 0) {
            message.warning('Please select nodes to duplicate');
            return;
        }
        
        const offsetX = 50;
        const offsetY = 50;
        const idMapping = {};
        
        // Find edges between selected nodes
        const nodeIds = new Set(nodesToDuplicate.map(n => n.id));
        const relatedEdges = edges.filter(edge => 
            nodeIds.has(edge.source) && nodeIds.has(edge.target)
        );
        
        // Create duplicated nodes - mark as selected
        const newNodes = nodesToDuplicate.map(node => {
            const newId = generateNodeId();
            idMapping[node.id] = newId;
            
            const nodeType = node.data?.nodeType || node.data?.type;
            const nodeDef = NODE_DEFINITIONS[nodeType];
            const isDecoration = nodeDef?.isDecoration === true;
            const zIndex = nodeType === 'shape_rectangle' ? -10 : (isDecoration ? 1 : 10);
            
            return {
                ...node,
                id: newId,
                position: {
                    x: node.position.x + offsetX,
                    y: node.position.y + offsetY
                },
                type: isDecoration ? 'decoration' : 'custom',
                zIndex,
                selected: true, // Mark new nodes as selected
                data: {
                    ...node.data,
                    config: { ...node.data?.config }
                }
            };
        });
        
        // Create new edges with updated source/target IDs
        const newEdges = relatedEdges.map(edge => ({
            ...edge,
            id: `edge_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            source: idMapping[edge.source],
            target: idMapping[edge.target],
            zIndex: edge.zIndex ?? 5 // Ensure edges appear above rectangles
        }));
        
        // Deselect all existing nodes first, then add new selected nodes
        const deselectedExistingNodes = nodes.map(node => ({
            ...node,
            selected: false
        }));
        dispatch(setNodes([...deselectedExistingNodes, ...newNodes]));
        
        // Add new edges
        newEdges.forEach(edge => dispatch(addEdgeAction(edge)));
        dispatch(saveToHistory());
        
        // Update local selectedNodes state with newly duplicated nodes
        setSelectedNodes(newNodes);
        
        // Clear single selected node since we now have multi-selection
        dispatch(setSelectedNode(null));
        dispatch(setDrawerVisible(false));
        
        message.success(`Duplicated ${newNodes.length} node(s)`);
    }, [selectedNodes, selectedNode, edges, nodes, generateNodeId, dispatch]);

    // Cut nodes (copy + delete)
    const handleCutNodes = useCallback(() => {
        handleCopyNodes();
        
        const nodesToDelete = selectedNodes.length > 0 
            ? selectedNodes 
            : (selectedNode ? [selectedNode] : []);
        
        if (nodesToDelete.length > 0) {
            nodesToDelete.forEach(node => dispatch(deleteNode(node.id)));
            dispatch(setSelectedNode(null));
            dispatch(setDrawerVisible(false));
            setSelectedNodes([]);
            dispatch(saveToHistory());
            message.success(`Cut ${nodesToDelete.length} node(s)`);
        }
    }, [handleCopyNodes, selectedNodes, selectedNode, dispatch]);

    // Close context menu - defined early as it's used by multiple callbacks
    const closeContextMenu = useCallback(() => {
        setContextMenu({ visible: false, x: 0, y: 0, nodeId: null });
    }, []);

    // Context menu handler
    const handleContextMenu = useCallback((event, node = null) => {
        event.preventDefault();
        event.stopPropagation(); // Prevent immediate close
        
        // Get click position
        let position = { x: event.clientX, y: event.clientY };
        
        // If right-clicking on canvas, get flow position for paste
        if (!node && rfInstance && rfInstance.screenToFlowPosition) {
            lastClickPos.current = rfInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY
            });
        }
        
        setContextMenu({
            visible: true,
            x: position.x,
            y: position.y,
            nodeId: node?.id || null
        });
    }, [rfInstance]);

    // Handle context menu item click
    const handleContextMenuAction = useCallback((action) => {
        closeContextMenu();
        
        switch (action) {
            case 'copy':
                handleCopyNodes();
                break;
            case 'paste':
                handlePasteNodes();
                break;
            case 'duplicate':
                handleDuplicateNodes();
                break;
            case 'cut':
                handleCutNodes();
                break;
            case 'delete':
                const nodesToDelete = selectedNodes.length > 0 
                    ? selectedNodes 
                    : (selectedNode ? [selectedNode] : []);
                if (nodesToDelete.length > 0) {
                    nodesToDelete.forEach(node => dispatch(deleteNode(node.id)));
                    dispatch(setSelectedNode(null));
                    dispatch(setDrawerVisible(false));
                    setSelectedNodes([]);
                    dispatch(saveToHistory());
                    message.success(`Deleted ${nodesToDelete.length} node(s)`);
                }
                break;
            default:
                break;
        }
    }, [closeContextMenu, handleCopyNodes, handlePasteNodes, handleDuplicateNodes, handleCutNodes, selectedNodes, selectedNode, dispatch]);

    // Keyboard shortcuts for copy/paste
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Check if focus is on an input field or editable element
            const activeElement = document.activeElement;
            const isInputFocused = activeElement && (
                activeElement.tagName === 'INPUT' || 
                activeElement.tagName === 'TEXTAREA' ||
                activeElement.isContentEditable ||
                activeElement.closest('.ant-input') ||
                activeElement.closest('.ant-select') ||
                activeElement.closest('.ant-input-number') ||
                activeElement.closest('.ant-picker') ||
                activeElement.closest('.ant-mentions') ||
                activeElement.closest('[contenteditable="true"]') ||
                activeElement.closest('.monaco-editor') ||
                activeElement.closest('.CodeMirror')
            );
            
            if (isInputFocused) return;
            
            // Check if user has selected text on the page
            const textSelection = window.getSelection();
            const hasTextSelected = textSelection && textSelection.toString().trim().length > 0;
            
            // If text is selected, let browser handle copy/cut normally
            if (hasTextSelected) return;
            
            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const ctrlKey = isMac ? event.metaKey : event.ctrlKey;
            
            // Check if there are nodes selected before copying/cutting
            const hasSelection = selectedNodes.length > 0 || selectedNode;
            
            if (ctrlKey && event.key === 'c') {
                // Only copy nodes if there's a node selection (not text)
                if (hasSelection) {
                    event.preventDefault();
                    handleCopyNodes();
                }
                // Otherwise, let browser handle normal text copy
            } else if (ctrlKey && event.key === 'v') {
                // Only paste if we have copied nodes
                if (copiedNodes.length > 0) {
                    event.preventDefault();
                    handlePasteNodes();
                }
            } else if (ctrlKey && event.key === 'd') {
                if (hasSelection) {
                    event.preventDefault();
                    handleDuplicateNodes();
                }
            } else if (ctrlKey && event.key === 'x') {
                if (hasSelection) {
                    event.preventDefault();
                    handleCutNodes();
                }
            }
        };
        
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleCopyNodes, handlePasteNodes, handleDuplicateNodes, handleCutNodes, selectedNodes, selectedNode, copiedNodes]);

    // Close context menu when clicking outside
    useEffect(() => {
        const handleClickOutside = () => {
            if (contextMenu.visible) {
                closeContextMenu();
            }
        };
        
        // Use capture phase to catch click before context menu
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('contextmenu', handleClickOutside);
        
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('contextmenu', handleClickOutside);
        };
    }, [contextMenu.visible, closeContextMenu]);

    // Handle node click
    const onNodeClick = useCallback((event, node) => {
        console.log('Node clicked:', node);
        
        // Always get the latest node data from Redux to ensure we have updated config
        // The node from ReactFlow callback might be stale if localNodes was used
        const latestNode = nodes.find(n => n.id === node.id) || node;
        
        dispatch(setSelectedNode(latestNode));
        dispatch(setDrawerVisible(true));
        // Clear canvas marker when clicking on a node
        setCanvasMarker(null);
        // Edge selection is handled by onSelectionChange
    }, [dispatch, nodes]);

    // Handle node context menu (right-click)
    const onNodeContextMenu = useCallback((event, node) => {
        event.preventDefault();
        
        // Select the node if not already in selection
        if (!selectedNodes.find(n => n.id === node.id)) {
            dispatch(setSelectedNode(node));
            setSelectedNodes([node]);
        }
        
        handleContextMenu(event, node);
    }, [selectedNodes, dispatch, handleContextMenu]);

    // Handle pane context menu (right-click on canvas)
    const onPaneContextMenu = useCallback((event) => {
        handleContextMenu(event, null);
    }, [handleContextMenu]);

    // Handle selection context menu (right-click on multi-selected nodes)
    const onSelectionContextMenu = useCallback((event, nodes) => {
        event.preventDefault();
        
        // Update selected nodes state
        if (nodes && nodes.length > 0) {
            setSelectedNodes(nodes);
        }
        
        handleContextMenu(event, nodes?.[0] || null);
    }, [handleContextMenu]);

    // Handle edge click
    const onEdgeClick = useCallback((event, edge) => {
        console.log('Edge clicked:', edge);
        event.stopPropagation();
        dispatch(setSelectedNode(null)); // Deselect node
        dispatch(setDrawerVisible(false)); // Hide drawer
        // Edge selection is handled by onSelectionChange
    }, [dispatch]);

    // Handle pane click (canvas background) to hide property drawer
    const onPaneClick = useCallback((event) => {
        console.log('Canvas clicked (background)');
        dispatch(setDrawerVisible(false));
        dispatch(setSelectedNode(null));
        setSelectedEdge(null); // Clear edge selection
        closeContextMenu(); // Close context menu on canvas click

        // Capture click position and show marker
        if (rfInstance && rfInstance.screenToFlowPosition) {
            const flowPosition = rfInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY
            });
            lastClickPos.current = flowPosition;
            
            // Get screen position relative to canvas container
            const containerRect = canvasContainerRef.current?.getBoundingClientRect();
            if (containerRect) {
                setCanvasMarker({
                    screenX: event.clientX - containerRect.left,
                    screenY: event.clientY - containerRect.top,
                    flowX: flowPosition.x,
                    flowY: flowPosition.y
                });
            }
        }
    }, [dispatch, rfInstance, closeContextMenu]);

    // Handle viewport move (pan/zoom) - clear marker since screen position is no longer valid
    const onMove = useCallback(() => {
        if (canvasMarker) {
            setCanvasMarker(null);
        }
    }, [canvasMarker]);

    // Handle node select from ExecutionResultsPanel
    const handleNodeSelectFromPanel = useCallback((nodeId) => {
        const node = nodes.find(n => n.id === nodeId);
        if (node && rfInstance) {
            // Select the node
            dispatch(setSelectedNode(node));
            dispatch(setDrawerVisible(true));
            
            // Center the view on the node with animation
            rfInstance.setCenter(
                node.position.x + 100, // Offset to center the node (assuming ~200px node width)
                node.position.y + 50,  // Offset to center the node (assuming ~100px node height)
                { duration: 500, zoom: 1 }
            );
        }
    }, [nodes, rfInstance, dispatch]);

    // Load flow when currentRule changes - sử dụng Redux action
    useEffect(() => {
        console.log('FlowCanvas: currentRule changed:', currentRule);
        
        if (!currentRule) {
            // Clear canvas if no rule
            console.log('FlowCanvas: No rule, clearing canvas');
            dispatch(clearFlow());
        }
        // Note: We don't dispatch setCurrentRule(currentRule) here because FlowBuilder already does it.
        // Dispatching it again here causes redundant updates and potential loops.
        
        // Sync variablesData with globalVariableService when rule changes
        const vars = globalVariableService.getAllVariables();
        setVariablesData(vars);
    }, [currentRule, dispatch]);

    // Notify parent when flow changes
    useEffect(() => {
        if (onFlowChange && typeof onFlowChange === 'function') {
            onFlowChange(nodes, edges);
        }
    }, [nodes, edges, onFlowChange]);

    // Sync selectedNode với Redux - removed vì selectedNode đã được quản lý trong Redux

    // Define custom node types - use MemoizedCustomNode for performance
    const nodeTypes = useMemo(() => ({
        custom: MemoizedCustomNode,
        decoration: DecorationNode
    }), []);

    // Define custom edge types with animation support
    const edgeTypes = useMemo(() => ({
        animated: AnimatedEdge,
        default: AnimatedEdge
    }), []);

    // Compute nodes with execution state - optimized for performance
    // Use activeNodes (local during drag, Redux otherwise) for smooth dragging
    const nodesWithExecutionState = useMemo(() => {
        return activeNodes.map(node => {
            const isError = errorNodeId === node.id;
            const isCompleted = !isError && completedNodeIdsSet.has(node.id);
            const isProcessing = executingNodeId === node.id;
            const isDebugTarget = debugNodeId === node.id;
            const isDebugPaused = debugPausedAtNodeId === node.id;
            
            // Ensure rectangle nodes always have zIndex -10
            const nodeType = node.data?.nodeType || node.data?.type;
            const correctZIndex = nodeType === 'shape_rectangle' ? -10 : node.zIndex;
            
            // Check if execution state is different from current node data
            // If nothing changed, return original node to preserve reference
            if (node.data.isProcessing === isProcessing &&
                node.data.isCompleted === isCompleted &&
                node.data.hasError === isError &&
                node.data.isDebugTarget === isDebugTarget &&
                node.data.isDebugPaused === isDebugPaused &&
                node.zIndex === correctZIndex) {
                return node;
            }
            
            return {
                ...node,
                zIndex: correctZIndex,
                data: {
                    ...node.data,
                    isProcessing,
                    isCompleted,
                    hasError: isError,
                    isDebugTarget,
                    isDebugPaused
                }
            };
        });
    }, [activeNodes, executingNodeId, completedNodeIdsSet, errorNodeId, debugNodeId, debugPausedAtNodeId]);

    // Convert animatingEdgeIds to Set for O(1) lookup
    const animatingEdgeIdsSet = useMemo(() => new Set(animatingEdgeIds), [animatingEdgeIds]);

    // Compute edges with animation state - optimized
    const edgesWithAnimationState = useMemo(() => {
        // If no animating edges, return edges with minimal transformation
        if (animatingEdgeIdsSet.size === 0) {
            return edges.map(edge => {
                // Skip if already has correct state
                if (edge.type === 'default' && edge.data?.isAnimating === false) {
                    return edge;
                }
                return {
                    ...edge,
                    type: 'default',
                    data: { ...edge.data, isAnimating: false }
                };
            });
        }
        
        return edges.map(edge => {
            const isAnimating = animatingEdgeIdsSet.has(edge.id);
            // Skip recreation if state unchanged
            if (edge.data?.isAnimating === isAnimating && 
                edge.type === (isAnimating ? 'animated' : 'default')) {
                return edge;
            }
            return {
                ...edge,
                type: isAnimating ? 'animated' : 'default',
                data: { ...edge.data, isAnimating },
                zIndex: edge.zIndex ?? 5 // Ensure edges appear above rectangles but below nodes
            };
        });
    }, [edges, animatingEdgeIdsSet]);

    // Handle connection between nodes
    const onConnect = useCallback((params) => {
        // Check if source node has edgeLabel
        const sourceNode = nodes.find(n => n.id === params.source);
        let edgeLabelProps = {};
        
        const nodeType = sourceNode?.data?.nodeType || sourceNode?.data?.type;

        if (sourceNode && nodeType === 'if_else') {
            if (params.sourceHandle === 'true' && sourceNode.data?.config?.edgeLabel) {
                edgeLabelProps = {
                    label: sourceNode.data.config.edgeLabel,
                    labelStyle: { fill: '#1890ff', fontWeight: 600, fontSize: 11 },
                    labelBgStyle: { fill: '#fff', fillOpacity: 0.8, rx: 4, ry: 4 }
                };
            } else if (params.sourceHandle === 'false') {
                edgeLabelProps = {
                    label: sourceNode.data?.config?.edgeLabelElse || 'Else',
                    labelStyle: { fill: '#ff4d4f', fontWeight: 600, fontSize: 11 },
                    labelBgStyle: { fill: '#fff', fillOpacity: 0.8, rx: 4, ry: 4 }
                };
            }
        }

        // Add label for edges from batch_end
        const srcNodeType = sourceNode?.data?.nodeType || sourceNode?.data?.type;
        if (srcNodeType === 'batch_end' && params.sourceHandle) {
            // Find the output name from dynamicOutputs config
            const dynamicOutputs = sourceNode?.data?.config?.dynamicOutputs || [];
            const outputDef = dynamicOutputs.find(out => (out.id || out) === params.sourceHandle);
            if (outputDef && outputDef.name) {
                edgeLabelProps = {
                    label: outputDef.name,
                    labelStyle: { fill: '#722ed1', fontWeight: 500, fontSize: 10 },
                    labelBgStyle: { fill: '#f9f0ff', fillOpacity: 0.95, rx: 3, ry: 3 }
                };
            }
        }

        const newEdge = {
            id: `edge_${Date.now()}`,
            ...params,
            ...edgeLabelProps,
            zIndex: 5 // Ensure edges appear above rectangles (zIndex: 0) but below nodes (zIndex: 10)
        };
        dispatch(addEdgeAction(newEdge));
        
        // Handle batch node dynamic handles sync
        const targetNode = nodes.find(n => n.id === params.target);
        const targetNodeType = targetNode?.data?.nodeType || targetNode?.data?.type;
        
        if (targetNodeType === 'batch_start') {
            // Need to sync after edge is added - use setTimeout to ensure edge is in state
            setTimeout(() => {
                // Re-query nodes and edges from current state
                const currentEdges = [...edges, newEdge];
                
                // Update batch_start input labels (not add/remove inputs)
                let updatedNodes = updateBatchStartInputLabels(nodes, currentEdges, params.target);
                if (updatedNodes) {
                    // Also sync batch_end outputs
                    const syncedNodes = syncBatchEndOutputs(updatedNodes, currentEdges, params.target);
                    dispatch(setNodes(syncedNodes || updatedNodes));
                }
            }, 0);
        }
        
        // If connecting to batch_end, find batch_start and sync outputs
        if (targetNodeType === 'batch_end') {
            setTimeout(() => {
                const currentEdges = [...edges, newEdge];
                const batchStartId = findBatchStartForBatchEnd(nodes, currentEdges, params.target);
                console.log('Connect to batch_end:', { batchEndId: params.target, foundBatchStartId: batchStartId });
                if (batchStartId) {
                    // First update batch_start input labels
                    let updatedNodes = updateBatchStartInputLabels(nodes, currentEdges, batchStartId);
                    if (updatedNodes) {
                        // Then sync to batch_end outputs
                        const syncedNodes = syncBatchEndOutputs(updatedNodes, currentEdges, batchStartId);
                        console.log('Synced batch_end outputs:', syncedNodes?.find(n => n.id === params.target)?.data?.config?.dynamicOutputs);
                        dispatch(setNodes(syncedNodes || updatedNodes));
                    }
                }
            }, 0);
        }
        
        // If source is batch_start, sync to find batch_end and update
        const sourceNodeType = sourceNode?.data?.nodeType || sourceNode?.data?.type;
        if (sourceNodeType === 'batch_start') {
            setTimeout(() => {
                const currentEdges = [...edges, newEdge];
                // Sync batch_end outputs when connecting from batch_start
                const syncedNodes = syncBatchEndOutputs(nodes, currentEdges, params.source);
                if (syncedNodes) {
                    dispatch(setNodes(syncedNodes));
                }
            }, 0);
        }
        
        dispatch(saveToHistory()); // Save to history for undo/redo
    }, [dispatch, nodes, edges]);

    // Handle drag over for drop functionality
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    // Handle drop to create new node
    const onDrop = useCallback((event) => {
        event.preventDefault();

        const reactFlowBounds = event.target.getBoundingClientRect();
        const nodeType = event.dataTransfer.getData('application/reactflow');

        if (!nodeType) return;

        const position = {
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
        };

        // Check if this is a decoration node
        const nodeDef = NODE_DEFINITIONS[nodeType];
        const isDecoration = nodeDef?.isDecoration === true;

        // Shape nodes should have lowest zIndex so other nodes appear on top
        const zIndex = nodeType === 'shape_rectangle' ? -10 : (isDecoration ? 1 : 10);

        const newNode = {
            // id: generated by redux
            type: isDecoration ? 'decoration' : 'custom',
            position,
            zIndex,
            data: {
                type: nodeType,
                nodeType: nodeType,
                config: {}
            },
        };

        dispatch(addNode(newNode));
        dispatch(saveToHistory()); // Save to history for undo/redo
        
        // Clear the canvas marker after dropping node
        setCanvasMarker(null);
        lastClickPos.current = null;
    }, [dispatch, nodes]);

    // Handle adding node from palette click
    const handleAddNodeFromPalette = useCallback((nodeType) => {
        console.log('Adding new node:', nodeType);
        
        let position;
        
        if (selectedNode) {
            // Case 1: Add next to selected node
            position = {
                x: selectedNode.position.x + 300,
                y: selectedNode.position.y
            };
        } else if (canvasMarker) {
            // Case 2: Use canvas marker position (from click on canvas)
            position = {
                x: canvasMarker.flowX,
                y: canvasMarker.flowY
            };
        } else if (lastClickPos.current) {
            // Case 3: Use last click position (fallback)
            position = lastClickPos.current;
        } else if (rfInstance && rfInstance.screenToFlowPosition) {
            // Case 4: Center of the view
            const paletteWidth = paletteCollapsed ? 0 : 300;
            const centerX = paletteWidth + (window.innerWidth - paletteWidth) / 2;
            
            position = rfInstance.screenToFlowPosition({
                x: centerX,
                y: window.innerHeight / 2
            });
        } else {
            // Fallback
            position = {
                x: Math.random() * 300 + 100, // Random position near center
                y: Math.random() * 200 + 100,
            };
        }

        // Check if this is a decoration node
        const nodeDef = NODE_DEFINITIONS[nodeType];
        const isDecoration = nodeDef?.isDecoration === true;

        // Shape nodes should have lowest zIndex so other nodes appear on top
        const zIndex = nodeType === 'shape_rectangle' ? -10 : (isDecoration ? 1 : 10);

        // Estimate node dimensions to center it at the marker position
        // Standard nodes are approximately 180x80, decoration nodes vary
        let nodeWidth = 180;
        let nodeHeight = 80;
        
        if (nodeType === 'shape_rectangle') {
            nodeWidth = 200;
            nodeHeight = 120;
        } else if (nodeType === 'comment') {
            nodeWidth = 200;
            nodeHeight = 100;
        } else if (nodeType === 'text_annotation') {
            nodeWidth = 100;
            nodeHeight = 30;
        }

        // Adjust position to center node at marker (only when using marker position)
        const centeredPosition = (canvasMarker || lastClickPos.current) ? {
            x: position.x - nodeWidth / 2,
            y: position.y - nodeHeight / 2
        } : position;

        const newNode = {
            // id: generated by redux
            type: isDecoration ? 'decoration' : 'custom',
            position: centeredPosition,
            zIndex,
            data: {
                type: nodeType,
                nodeType: nodeType,
                config: {}
            },
        };

        dispatch(addNode(newNode));
        dispatch(saveToHistory()); // Save to history for undo/redo
        
        // Clear the canvas marker after creating node
        setCanvasMarker(null);
        lastClickPos.current = null;
        
        message.success(`Added ${nodeDef?.name || nodeType} node`);
    }, [dispatch, nodes, selectedNode, rfInstance, paletteCollapsed, canvasMarker]);

    // Handle connection between nodes    // Handle update node configuration with auto-save
    const handleUpdateNode = useCallback((nodeId, newData) => {
        // Clear localNodes to ensure Redux update is visible immediately
        // This is important when user edits node config while localNodes exists from previous drag
        if (localNodes) {
            setLocalNodes(null);
        }
        
        dispatch(updateNode({ nodeId, newData }));
        dispatch(saveToHistory()); // Save to history for undo/redo
        
        // Check if batch_start dynamicInputs changed - sync to batch_end
        const node = nodes.find(n => n.id === nodeId);
        const nodeType = node?.data?.nodeType || node?.data?.type;
        if (nodeType === 'batch_start' && newData.config?.dynamicInputs) {
            // Need to sync batch_end outputs
            setTimeout(() => {
                // Create updated nodes array with the new config
                const updatedNodes = nodes.map(n => 
                    n.id === nodeId ? { ...n, data: newData } : n
                );
                const syncedNodes = syncBatchEndOutputs(updatedNodes, edges, nodeId);
                if (syncedNodes) {
                    dispatch(setNodes(syncedNodes));
                }
            }, 0);
        }
        
        // Check if we need to update edge labels (e.g. for if_else nodes)
        if (newData.config && newData.config.edgeLabel) {
            const connectedEdges = edges.filter(e => e.source === nodeId);
            // Only update if there are connected edges and they don't have the label yet
            const needsUpdate = connectedEdges.some(edge => 
                (edge.sourceHandle === 'true' && edge.label !== newData.config.edgeLabel) ||
                (edge.sourceHandle === 'false' && edge.label !== (newData.config.edgeLabelElse || 'Else'))
            );
            
            if (needsUpdate) {
                const newEdges = edges.map(edge => {
                    if (edge.source === nodeId) {
                        if (edge.sourceHandle === 'true') {
                            return { 
                                ...edge, 
                                label: newData.config.edgeLabel,
                                labelStyle: { fill: '#1890ff', fontWeight: 600, fontSize: 11 },
                                labelBgStyle: { fill: '#fff', fillOpacity: 0.8, rx: 4, ry: 4 }
                            };
                        } else if (edge.sourceHandle === 'false') {
                            return { 
                                ...edge, 
                                label: newData.config.edgeLabelElse || 'Else',
                                labelStyle: { fill: '#ff4d4f', fontWeight: 600, fontSize: 11 },
                                labelBgStyle: { fill: '#fff', fillOpacity: 0.8, rx: 4, ry: 4 }
                            };
                        }
                    }
                    return edge;
                });
                dispatch(setEdges(newEdges));
            }
        }

        // Auto-save: Notify parent with updated nodes (debounced)
        if (onFlowChange && typeof onFlowChange === 'function') {
            setTimeout(() => {
                // Get fresh state after dispatch
                const currentNodes = nodes.map(node => 
                    node.id === nodeId ? { ...node, data: newData } : node
                );
                onFlowChange(currentNodes, edges);
            }, 100);
        }
        
        console.log('Node auto-saved:', nodeId, newData);
    }, [dispatch, onFlowChange, nodes, edges, localNodes]);

    // Delete node
    const handleDeleteNode = useCallback((nodeId) => {
        dispatch(deleteNode(nodeId));
        dispatch(saveToHistory()); // Save to history for undo/redo
        message.success('Node deleted');
    }, [dispatch]);

    // Delete selected elements (node or edge)
    const handleDeleteSelected = useCallback(() => {
        let hasChanges = false;
        
        if (selectedNode) {
            dispatch(deleteNode(selectedNode.id));
            dispatch(setSelectedNode(null));
            dispatch(setDrawerVisible(false));
            hasChanges = true;
            message.success('Node deleted');
        }
        
        if (selectedEdge) {
            const newEdges = edges.filter(e => e.id !== selectedEdge.id);
            dispatch(setEdges(newEdges));
            setSelectedEdge(null);
            hasChanges = true;
            message.success('Connection deleted');
        }

        if (hasChanges) {
            dispatch(saveToHistory());
        }
    }, [dispatch, selectedNode, selectedEdge, edges]);

    // Lấy currentRule từ Redux state
    const reduxCurrentRule = useSelector(selectCurrentRule);

    // Save Workflow only - không liên quan đến rule
    const handleSaveWorkflow = useCallback(async () => {
        console.log('handleSaveWorkflow called with currentRule from Redux:', reduxCurrentRule);
        
        // Check if rule has workflowId
        if (!reduxCurrentRule || !reduxCurrentRule.workflowId) {
            message.error('Không tìm thấy workflow. Vui lòng tạo rule trước.');
            return;
        }
        
        dispatch(setSaving(true));
        
        try {
            const currentNodes = nodes;
            const currentEdges = edges;
            
            // Prepare workflow data
            const workflowData = {
                name: reduxCurrentRule.workflow?.name || `Workflow - ${reduxCurrentRule.name}`,
                description: reduxCurrentRule.workflow?.description || reduxCurrentRule.description,
                type: 'ads_optimization',
                status: 'draft',
                nodes: currentNodes.map(node => {
                    const { inputSchema, outputSchema, ...cleanData } = node.data;
                    return {
                        id: node.id,
                        type: node.data?.nodeType || node.data?.type || node.type,
                        position: node.position,
                        data: {
                            nodeType: node.data?.nodeType || node.data?.type || node.type,
                            type: node.data?.type || node.data?.nodeType || node.type,
                            config: node.data?.config || {},
                            label: node.data?.label,
                            ...cleanData
                        }
                    };
                }),
                edges: currentEdges.map(edge => ({
                    id: edge.id,
                    source: edge.source,
                    target: edge.target,
                    sourceHandle: edge.sourceHandle,
                    targetHandle: edge.targetHandle
                })),
                variables: globalVariableService.getAllVariables(),
                metadata: {
                    lastSaved: new Date().toISOString(),
                    nodeCount: currentNodes.length,
                    edgeCount: currentEdges.length
                }
            };
            
            // Update existing workflow
            const workflowResponse = await WorkflowAPIService.updateWorkflow(reduxCurrentRule.workflowId, workflowData);
            
            if (workflowResponse.success) {
                message.success('Lưu workflow thành công!');
            } else {
                throw new Error(workflowResponse.message || 'Failed to save workflow');
            }
        } catch (error) {
            console.error('Error saving workflow:', error);
            message.error('Lỗi khi lưu workflow: ' + error.message);
        } finally {
            dispatch(setSaving(false));
        }
    }, [nodes, edges, dispatch, reduxCurrentRule]);

    // Clear all nodes and edges
    const handleClearFlow = () => {
        dispatch(clearFlow());
        message.success('Flow cleared');
    };

    // Run flow with proper flow execution
    // Handle clearing the canvas    // Find nodes connected from a given node
    const findConnectedNodes = (nodeId) => {
        const connectedEdges = edges.filter(edge => edge.source === nodeId);
        const connectedNodeIds = connectedEdges.map(edge => edge.target);
        return nodes.filter(node => connectedNodeIds.includes(node.id));
    };

    // Execute node that requires input
    const executeNodeWithInput = (node) => {
        const inputFields = node.data?.config?.inputFields || [];
        
        if (inputFields.length > 0) {
            setInputModal({
                visible: true,
                nodeData: node
            });
        } else {
            message.warning('This input node has no configured input fields');
        }
    };

    // Handle input modal submission
    const handleInputSubmit = (variableData) => {
        // Store variables in global service
        globalVariableService.setVariables(variableData);
        
        // Close modal
        setInputModal({ visible: false, nodeData: null });
        
        // Show success message
        const variableNames = Object.keys(variableData);
        message.success(`Stored ${variableNames.length} variables: ${variableNames.join(', ')}`);
        
        // Continue workflow execution to next connected nodes
        const currentNode = inputModal.nodeData;
        if (currentNode) {
            const nextNodes = findConnectedNodes(currentNode.id);
            if (nextNodes.length > 0) {
                message.info('Continuing to next node...');
                // Note: executeWorkflowFromNode will be handled by parent FlowBuilder component
            } else {
                message.success('🎉 Workflow completed! (No more connected nodes)');
            }
        }
    };

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', position: 'relative' }}>
            {/* Custom Styles for ReactFlow */}
            <style>
                {`
                    .react-flow__edge.selected .react-flow__edge-path {
                        stroke: #1890ff !important;
                        stroke-width: 3 !important;
                    }
                `}
            </style>
            {/* Node Palette - with collapse functionality */}
            <div style={{ 
                width: paletteCollapsed ? '0px' : '320px',
                transition: 'width 0.3s ease',
                overflow: 'hidden',
                borderRight: paletteCollapsed ? 'none' : '1px solid #f0f0f0'
            }}>
                <NodePalette onNodeAdd={handleAddNodeFromPalette} />
            </div>

            {/* Main Flow Area with Rule Info */}
            <div style={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative'
            }}>
                {/* Rule Info Header */}
                {ruleInfo && (
                    <div style={{
                        padding: '10px 5px',
                        backgroundColor: '#fafafa',
                        borderBottom: '1px solid #f0f0f0',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        zIndex: 10
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* Toggle Palette Button */}
                            <Button
                                type="text"
                                icon={<LeftOutlined style={{ 
                                    transform: paletteCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                                    transition: 'transform 0.3s ease'
                                }} />}
                                onClick={() => dispatch(setPaletteCollapsed(!paletteCollapsed))}
                                title={paletteCollapsed ? 'Mở rộng Node Palette' : 'Thu gọn Node Palette'}
                            />
                            
                            {/* Rule Title - Clickable to go back to rules list */}
                            <div 
                                title="Quay về danh sách Rules"
                            >
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    {ruleInfo.name}
                                </Typography.Title>
                                {/* <Text type="secondary" style={{ fontSize: '14px', display: 'block'}}>
                                    {ruleInfo.description}
                                </Text> */}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <Space>
                            {canUpdateWorkflow && (
                                <Button
                                    type="primary"
                                    icon={<SaveOutlined />}
                                    onClick={handleSaveWorkflow}
                                    loading={saving}
                                >
                                    Save Workflow
                                </Button>
                            )}
                        </Space>
                    </div>
                )}

                {/* Flow Canvas */}
                <div ref={canvasContainerRef} style={{ flex: 1, position: 'relative', backgroundColor: '#fafafa' }}>
                    <ReactFlow
                        onInit={setRfInstance}
                        nodes={nodesWithExecutionState}
                        edges={edgesWithAnimationState}
                        onNodesChange={onNodesChange}
                        onNodeDragStop={onNodeDragStop}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        onNodeClick={onNodeClick}
                        onNodeContextMenu={onNodeContextMenu}
                        onEdgeClick={onEdgeClick}
                        onPaneClick={onPaneClick}
                        onPaneContextMenu={onPaneContextMenu}
                        onSelectionContextMenu={onSelectionContextMenu}
                        onSelectionChange={onSelectionChange}
                        onMove={onMove}
                        nodeTypes={nodeTypes}
                        edgeTypes={edgeTypes}
                        fitView
                        minZoom={0.1}
                        maxZoom={2}
                        defaultEdgeOptions={{
                            style: { strokeWidth: 2, stroke: '#b1b1b7' },
                            type: 'default',
                            animated: false,
                            interactionWidth: 20, // Make it easier to click
                            selectable: true,
                            deletable: true,
                            focusable: true
                        }}
                        connectionLineStyle={{ strokeWidth: 2, stroke: '#bfbfbf' }}
                        style={{ background: '#fafafa', width: '100%', height: '100%' }}
                        panOnDrag={!selectionMode}
                        selectionOnDrag={selectionMode}
                        panOnScroll={true}
                        selectionMode={selectionMode ? 'partial' : 'full'}
                    >
                        <Background
                            color="#e8e8e8"
                            gap={20}
                            size={0.5}
                            variant="dots"
                        />
                        
                        <Controls
                            style={{
                                background: 'rgba(255,255,255,0.9)',
                                border: '1px solid #f0f0f0',
                                borderRadius: '6px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                            }}
                        />

                        {/* Execute Workflow Button - Top Left */}
                        <Panel position="top-left">
                            <Space>
                                <div style={{ 
                                    display: 'flex', 
                                    backgroundColor: '#fff', 
                                    borderRadius: '6px', 
                                    border: '1px solid #d9d9d9',
                                    overflow: 'hidden',
                                    marginRight: '8px'
                                }}>
                                    <Button
                                        type={!selectionMode ? 'primary' : 'text'}
                                        icon={<DragOutlined />}
                                        onClick={() => setSelectionMode(false)}
                                        title="Pan Mode (Hand)"
                                        style={{ 
                                            borderRadius: 0, 
                                            border: 'none',
                                            backgroundColor: !selectionMode ? '#1890ff' : 'transparent',
                                            color: !selectionMode ? '#fff' : 'rgba(0, 0, 0, 0.85)'
                                        }}
                                    />
                                    <Button
                                        type={selectionMode ? 'primary' : 'text'}
                                        icon={<SelectOutlined />}
                                        onClick={() => setSelectionMode(true)}
                                        title="Selection Mode (Box)"
                                        style={{ 
                                            borderRadius: 0, 
                                            border: 'none',
                                            backgroundColor: selectionMode ? '#1890ff' : 'transparent',
                                            color: selectionMode ? '#fff' : 'rgba(0, 0, 0, 0.85)'
                                        }}
                                    />
                                </div>

                                {/* Debug breakpoint button - show when node is selected and has execute permission */}
                                {canExecuteWorkflow && selectedNode && !debugNodeId && (
                                    <Button
                                        icon={<AimOutlined />}
                                        onClick={() => onSetDebugNode && onSetDebugNode(selectedNode.id)}
                                        style={{
                                            borderRadius: '6px',
                                            height: '32px',
                                            backgroundColor: '#fff7e6',
                                            borderColor: '#ffa940',
                                            color: '#fa8c16',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                        }}
                                        title={`Set debug breakpoint at "${selectedNode.data?.label || selectedNode.data?.nodeType || 'this node'}"`}
                                    >
                                        Debug to here
                                    </Button>
                                )}

                                {/* Show debug info and clear button when debug node is set */}
                                {canExecuteWorkflow && debugNodeId && (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: '#fff7e6',
                                        border: '1px solid #ffa940',
                                        borderRadius: '6px',
                                        padding: '0 8px',
                                        height: '32px',
                                        gap: '8px'
                                    }}>
                                        <BugOutlined style={{ color: '#fa8c16' }} />
                                        <Text style={{ color: '#fa8c16', fontSize: '12px' }}>
                                            Debug: {nodes.find(n => n.id === debugNodeId)?.data?.label || 
                                                   nodes.find(n => n.id === debugNodeId)?.data?.nodeType || 
                                                   'Node'}
                                        </Text>
                                        <CloseOutlined 
                                            style={{ color: '#fa8c16', cursor: 'pointer', fontSize: '10px' }}
                                            onClick={() => onClearDebugNode && onClearDebugNode()}
                                            title="Clear debug breakpoint"
                                        />
                                    </div>
                                )}

                                {/* Execute or Debug button - only show if has execute permission */}
                                {canExecuteWorkflow && (debugNodeId ? (
                                    <Button
                                        type="primary"
                                        icon={<BugOutlined />}
                                        onClick={() => onDebugToNode && onDebugToNode(debugNodeId)}
                                        loading={isExecuting}
                                        disabled={!nodes.length || isExecuting}
                                        style={{
                                            borderRadius: '6px',
                                            height: '32px',
                                            backgroundColor: '#fa8c16',
                                            borderColor: '#fa8c16',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {isExecuting ? 'Debugging...' : 'Debug'}
                                    </Button>
                                ) : (
                                    <Button
                                        type={isExecuting ? "default" : "primary"}
                                        icon={<PlayCircleOutlined />}
                                        onClick={onExecuteWorkflow}
                                        loading={isExecuting}
                                        disabled={!nodes.length || isExecuting}
                                        style={{
                                            borderRadius: '6px',
                                            height: '32px',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.06)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '6px'
                                        }}
                                    >
                                        {isExecuting ? 'Executing...' : 'Execute Workflow'}
                                    </Button>
                                ))}

                                <Button
                                    icon={<DatabaseOutlined />}
                                    onClick={() => {
                                        const vars = globalVariableService.getAllVariables();
                                        setVariablesData(vars);
                                        setVariablesPanel(true);
                                    }}
                                    style={{
                                        borderRadius: '6px',
                                        height: '32px',
                                        backgroundColor: '#ffffff',
                                        borderColor: '#d9d9d9',
                                        color: '#595959',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                    }}
                                >
                                    Variables
                                </Button>

                                {/* Debug Step Controls - show when paused at a node */}
                                {isDebugMode && debugPausedAtNodeId && !isExecuting && (
                                    <>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: '#fff7e6',
                                            border: '1px solid #ffa940',
                                            borderRadius: '6px',
                                            padding: '0 12px',
                                            height: '32px',
                                            gap: '8px'
                                        }}>
                                            <PauseCircleOutlined style={{ color: '#fa8c16' }} />
                                            <Text style={{ color: '#fa8c16', fontSize: '12px', fontWeight: 500 }}>
                                                Paused at: {nodes.find(n => n.id === debugPausedAtNodeId)?.data?.label || 
                                                           nodes.find(n => n.id === debugPausedAtNodeId)?.data?.nodeType || 
                                                           'Node'}
                                            </Text>
                                        </div>
                                        <Button
                                            type="primary"
                                            icon={<StepForwardOutlined />}
                                            onClick={() => onDebugNextNode && onDebugNextNode()}
                                            style={{
                                                borderRadius: '6px',
                                                height: '32px',
                                                backgroundColor: '#fa8c16',
                                                borderColor: '#fa8c16',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                            }}
                                            title="Execute next node"
                                        >
                                            Next
                                        </Button>
                                        <Button
                                            danger
                                            icon={<StopOutlined />}
                                            onClick={() => onStopDebug && onStopDebug()}
                                            style={{
                                                borderRadius: '6px',
                                                height: '32px',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                            }}
                                            title="Stop debugging"
                                        >
                                            Stop
                                        </Button>
                                    </>
                                )}
                            </Space>
                        </Panel>

                        {/* Clear Button - Top Right */}
                        <Panel position="top-right">
                            <Space>
                                {/* <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleDeleteSelected}
                                    disabled={!selectedNode && !selectedEdge}
                                    style={{
                                        borderRadius: '6px',
                                        height: '32px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                    }}
                                    title="Delete selected node or connection"
                                >
                                    Delete
                                </Button> */}
                                {/* <Button
                                    icon={<ClearOutlined />}
                                    onClick={handleClearFlow}
                                    style={{
                                        borderRadius: '6px',
                                        height: '32px',
                                        backgroundColor: '#ffffff',
                                        borderColor: '#d9d9d9',
                                        color: '#595959',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
                                    }}
                                >
                                    Clear
                                </Button> */}
                            </Space>
                        </Panel>

                        {/* Info Panel */}
                        <Panel position="bottom-left">
                            <Space size={8} style={{marginLeft:37}}>
                                {/* Execution Results Button - only show if there are results */}
                                {executionResult && (
                                    <Button
                                        type={executionResult.success ? 'primary' : 'default'}
                                        icon={executionResult.success ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                                        onClick={handleShowExecutionPanel}
                                        style={{
                                            borderRadius: '6px',
                                            backgroundColor: executionResult.success ? '#52c41a' : '#fff',
                                            borderColor: executionResult.success ? '#52c41a' : '#ff4d4f',
                                            color: executionResult.success ? '#fff' : '#ff4d4f',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        Execution Results
                                    </Button>
                                )}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px'
                                }}>
                                    {/* Toggle Palette Button - only show if no rule header */}
                                    {!ruleInfo && (
                                        <Button
                                            type="text"
                                            size="small"
                                            icon={<LeftOutlined style={{ 
                                                transform: paletteCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.3s ease',
                                                fontSize: '10px'
                                            }} />}
                                            onClick={() => dispatch(setPaletteCollapsed(!paletteCollapsed))}
                                            title={paletteCollapsed ? 'Mở rộng Node Palette' : 'Thu gọn Node Palette'}
                                            style={{
                                                backgroundColor: paletteCollapsed ? '#e6f7ff' : 'transparent',
                                                borderColor: paletteCollapsed ? '#91d5ff' : 'transparent',
                                                height: '20px',
                                                width: '20px',
                                                padding: 0
                                            }}
                                        />
                                    )}
                                    <Text type="secondary" style={{ fontWeight: 500, color: '#8c8c8c' }}>
                                        Nodes: {nodes.length} | Connections: {edges.length} | Auto-save: ON
                                    </Text>
                                </div>
                            </Space>
                        </Panel>

                        {/* Workflow State Panel - Bottom Right */}
                        <Panel position="bottom-right">
                            <div style={{
                                maxWidth: '300px'
                            }}>
                                <Text type="secondary" style={{ fontWeight: 500, color: '#8c8c8c' }}>
                                    Last Modified: {new Date().toLocaleTimeString()}
                                </Text>
                            </div>
                        </Panel>
                    </ReactFlow>
                    
                    {/* Canvas Marker - Red dot showing where new node will be created */}
                    {canvasMarker && (
                        <div
                            style={{
                                position: 'absolute',
                                left: canvasMarker.screenX,
                                top: canvasMarker.screenY,
                                transform: 'translate(-50%, -50%)',
                                zIndex: 1000,
                                pointerEvents: 'none',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 4
                            }}
                        >
                            {/* Red dot */}
                            <div
                                style={{
                                    width: 14,
                                    height: 14,
                                    borderRadius: '50%',
                                    backgroundColor: '#ff4d4f',
                                    border: '2px solid #fff',
                                    boxShadow: '0 2px 8px rgba(255, 77, 79, 0.5)',
                                    animation: 'pulse 1.5s ease-in-out infinite'
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* Property Panel - Overlay on right side of flow canvas */}
                {drawerVisible && !localShowExecutionPanel && (
                    <div style={{
                        position: 'absolute',
                        right: 0,
                        top: ruleInfo ? '53px' : '0', // Start below header if ruleInfo exists
                        bottom: 0,
                        width: '500px',
                        backgroundColor: '#fff',
                        borderLeft: '1px solid #f0f0f0',
                        zIndex: 1000,
                        boxShadow: '-2px 0 8px rgba(0,0,0,0.1)',
                        transition: 'transform 0.3s ease',
                        transform: drawerVisible ? 'translateX(0)' : 'translateX(100%)'
                    }}>
                        <PropertyDrawer
                            visible={drawerVisible}
                            onClose={() => {
                                dispatch(setDrawerVisible(false));
                            }}
                            selectedNode={selectedNode}
                            onUpdateNode={handleUpdateNode}
                            onDeleteNode={handleDeleteNode}
                            isFixedPanel={true}
                            nodes={nodes}
                            edges={edges}
                        />
                    </div>
                )}

                {/* Execution Results Panel - Overlay on right side of flow canvas */}
                <Drawer
                    title={
                        <Space>
                            {executionResult?.success ? (
                                <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
                            ) : (
                                <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
                            )}
                            <span>Execution Results</span>
                        </Space>
                    }
                    placement="right"
                    width={500}
                    onClose={handleCloseExecutionPanel}
                    open={localShowExecutionPanel}
                    mask={false}
                    getContainer={false}
                    style={{ position: 'absolute', top: ruleInfo ? '53px' : '0' }}
                    styles={{ body: { padding: 0 } }}
                >
                    <ExecutionResultsPanel 
                        executionResult={executionResult}
                        nodes={nodes}
                        onNodeSelect={handleNodeSelectFromPanel}
                        selectedNodeId={selectedNode?.id}
                    />
                </Drawer>
            </div>

            {/* Input Modal for workflow execution */}
            <InputModal
                visible={inputModal.visible}
                onClose={() => setInputModal({ visible: false, nodeData: null })}
                onSubmit={handleInputSubmit}
                nodeConfig={inputModal.nodeData?.data?.config}
                nodeData={inputModal.nodeData}
            />

            {/* Global Variables Modal */}
            <Modal
                title={
                    <Space>
                        <DatabaseOutlined />
                        <span>Global Variables</span>
                    </Space>
                }
                open={variablesPanel}
                onCancel={() => {
                    setVariablesPanel(false);
                    setNewVarName('');
                    setNewVarValue('');
                }}
                footer={[
                    <Button key="clear" danger onClick={() => {
                        globalVariableService.clearAll();
                        setVariablesData({});
                        message.success('All variables cleared');
                    }}>
                        Clear All
                    </Button>,
                    <Button key="close" type="primary" onClick={() => {
                        setVariablesPanel(false);
                        setNewVarName('');
                        setNewVarValue('');
                    }}>
                        Close
                    </Button>
                ]}
                width={700}
            >
                {/* Quick Add Dynamic Variables */}
                <div style={{
                    padding: '12px 16px',
                    marginBottom: 12,
                    background: '#f6ffed',
                    borderRadius: 8,
                    border: '1px solid #b7eb8f'
                }}>
                    <Text strong style={{ display: 'block', marginBottom: 8, color: '#52c41a' }}>
                        <ThunderboltOutlined /> Quick Add Dynamic Variables
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 12 }}>
                        These variables are automatically resolved at execution time
                    </Text>
                    <Space wrap>
                        <Button
                            size="small"
                            icon={<CalendarOutlined />}
                            onClick={() => {
                                globalVariableService.setVariable('TODAY', '{{TODAY}}');
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                message.success('Added TODAY variable (YYYY-MM-DD format)');
                            }}
                        >
                            TODAY
                        </Button>
                        <Button
                            size="small"
                            icon={<CalendarOutlined />}
                            onClick={() => {
                                globalVariableService.setVariable('YESTERDAY', '{{YESTERDAY}}');
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                message.success('Added YESTERDAY variable');
                            }}
                        >
                            YESTERDAY
                        </Button>
                        <Button
                            size="small"
                            icon={<ClockCircleOutlined />}
                            onClick={() => {
                                globalVariableService.setVariable('NOW', '{{NOW}}');
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                message.success('Added NOW variable (ISO datetime)');
                            }}
                        >
                            NOW
                        </Button>
                        <Button
                            size="small"
                            icon={<CalendarOutlined />}
                            onClick={() => {
                                globalVariableService.setVariable('THIS_MONTH', '{{THIS_MONTH}}');
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                message.success('Added THIS_MONTH variable (YYYY-MM format)');
                            }}
                        >
                            THIS_MONTH
                        </Button>
                        <Button
                            size="small"
                            icon={<CalendarOutlined />}
                            onClick={() => {
                                globalVariableService.setVariable('THIS_YEAR', '{{THIS_YEAR}}');
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                message.success('Added THIS_YEAR variable');
                            }}
                        >
                            THIS_YEAR
                        </Button>
                    </Space>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                {/* Add New Variable Form */}
                <div style={{
                    padding: '16px',
                    marginBottom: 16,
                    background: '#e6f7ff',
                    borderRadius: 8,
                    border: '1px solid #91d5ff'
                }}>
                    <Text strong style={{ display: 'block', marginBottom: 12, color: '#1890ff' }}>
                        <PlusOutlined /> Add Custom Variable
                    </Text>
                    <Space.Compact style={{ width: '100%' }}>
                        <Input
                            placeholder="Variable name"
                            value={newVarName}
                            onChange={(e) => setNewVarName(e.target.value)}
                            style={{ width: '30%' }}
                        />
                        <Input
                            placeholder="Variable value (string, number, or JSON)"
                            value={newVarValue}
                            onChange={(e) => setNewVarValue(e.target.value)}
                            style={{ width: '55%' }}
                            onPressEnter={() => {
                                if (!newVarName.trim()) {
                                    message.warning('Please enter a variable name');
                                    return;
                                }
                                // Try to parse as JSON, otherwise use as string
                                let parsedValue = newVarValue;
                                try {
                                    parsedValue = JSON.parse(newVarValue);
                                } catch (e) {
                                    // Keep as string if not valid JSON
                                    parsedValue = newVarValue;
                                }
                                globalVariableService.setVariable(newVarName.trim(), parsedValue);
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                setNewVarName('');
                                setNewVarValue('');
                                message.success(`Variable '${newVarName.trim()}' added`);
                            }}
                        />
                        <Button
                            type="primary"
                            onClick={() => {
                                if (!newVarName.trim()) {
                                    message.warning('Please enter a variable name');
                                    return;
                                }
                                // Try to parse as JSON, otherwise use as string
                                let parsedValue = newVarValue;
                                try {
                                    parsedValue = JSON.parse(newVarValue);
                                } catch (e) {
                                    // Keep as string if not valid JSON
                                    parsedValue = newVarValue;
                                }
                                globalVariableService.setVariable(newVarName.trim(), parsedValue);
                                const updated = globalVariableService.getAllVariables();
                                setVariablesData(updated);
                                setNewVarName('');
                                setNewVarValue('');
                                message.success(`Variable '${newVarName.trim()}' added`);
                            }}
                            style={{ width: '15%' }}
                        >
                            Add
                        </Button>
                    </Space.Compact>
                    <Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                        Tip: Enter JSON for objects/arrays (e.g., {'{"key": "value"}'} or [1, 2, 3])
                    </Text>
                </div>

                {/* Variables List */}
                {Object.keys(variablesData).length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                        <DatabaseOutlined style={{ fontSize: 48, marginBottom: 16 }} />
                        <p>No variables stored yet.</p>
                        <p style={{ fontSize: 12 }}>Add variables above or they will appear when workflow nodes store them.</p>
                    </div>
                ) : (
                    <div style={{ maxHeight: 350, overflow: 'auto' }}>
                        <Text strong style={{ display: 'block', marginBottom: 12, color: '#595959' }}>
                            Stored Variables ({Object.keys(variablesData).length})
                        </Text>
                        {Object.entries(variablesData).map(([name, details]) => {
                            // Check if this is a dynamic variable (value starts with {{)
                            const valueStr = typeof details?.value === 'string' ? details.value : '';
                            const isDynamic = valueStr.startsWith('{{') && valueStr.endsWith('}}');
                            
                            return (
                                <div
                                    key={name}
                                    style={{
                                        padding: '12px 16px',
                                        marginBottom: 8,
                                        background: isDynamic ? '#f6ffed' : '#f5f5f5',
                                        borderRadius: 6,
                                        border: isDynamic ? '1px solid #b7eb8f' : '1px solid #e8e8e8'
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                        <Space>
                                            <Text strong style={{ color: '#1890ff' }}>{name}</Text>
                                            {isDynamic && (
                                                <Tag color="green" icon={<ClockCircleOutlined />}>Dynamic</Tag>
                                            )}
                                        </Space>
                                        <Space>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                Type: {isDynamic ? 'dynamic' : (details?.type || 'unknown')}
                                            </Text>
                                            <Button
                                                size="small"
                                                type="text"
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={() => {
                                                    globalVariableService.clearVariable(name);
                                                    const updated = globalVariableService.getAllVariables();
                                                    setVariablesData(updated);
                                                    message.success(`Variable '${name}' deleted`);
                                                }}
                                            />
                                        </Space>
                                    </div>
                                    <div style={{ background: '#fff', padding: 8, borderRadius: 4, fontSize: 12, maxHeight: 150, overflow: 'auto' }}>
                                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                            {isDynamic ? (
                                                <Text code>{valueStr}</Text>
                                            ) : (
                                                typeof details?.value === 'object' 
                                                    ? JSON.stringify(details?.value, null, 2)
                                                    : String(details?.value)
                                            )}
                                        </pre>
                                        {isDynamic && (
                                            <Text type="secondary" style={{ fontSize: 10, marginTop: 4, display: 'block' }}>
                                                This value will be resolved at execution time (e.g., TODAY → 2025-12-08)
                                            </Text>
                                        )}
                                    </div>
                                    {details?.timestamp && !isDynamic && (
                                        <Text type="secondary" style={{ fontSize: 10, marginTop: 4, display: 'block' }}>
                                            Last updated: {new Date(details.timestamp).toLocaleString()}
                                        </Text>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal>

            {/* Context Menu for copy/paste */}
            {contextMenu.visible && (
                <div
                    style={{
                        position: 'fixed',
                        left: contextMenu.x,
                        top: contextMenu.y,
                        zIndex: 10000,
                        background: '#fff',
                        borderRadius: '8px',
                        boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12), 0 9px 28px 8px rgba(0, 0, 0, 0.05)',
                        padding: '4px 0',
                        minWidth: '160px'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Copy - only show when nodes are selected */}
                    {(selectedNodes.length > 0 || selectedNode) && (
                        <div
                            style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                color: '#262626'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            onClick={() => handleContextMenuAction('copy')}
                        >
                            <CopyOutlined style={{ fontSize: '14px' }} />
                            <span>Copy</span>
                            <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>⌘C</span>
                        </div>
                    )}
                    
                    {/* Cut - only show when nodes are selected */}
                    {(selectedNodes.length > 0 || selectedNode) && (
                        <div
                            style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                color: '#262626'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            onClick={() => handleContextMenuAction('cut')}
                        >
                            <ScissorOutlined style={{ fontSize: '14px' }} />
                            <span>Cut</span>
                            <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>⌘X</span>
                        </div>
                    )}
                    
                    {/* Paste - always show if clipboard has content */}
                    {copiedNodes.length > 0 && (
                        <div
                            style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                color: '#262626'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            onClick={() => handleContextMenuAction('paste')}
                        >
                            <SnippetsOutlined style={{ fontSize: '14px' }} />
                            <span>Paste ({copiedNodes.length})</span>
                            <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>⌘V</span>
                        </div>
                    )}
                    
                    {/* Duplicate - only show when nodes are selected */}
                    {(selectedNodes.length > 0 || selectedNode) && (
                        <>
                            <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0' }} />
                            <div
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    color: '#262626'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                onClick={() => handleContextMenuAction('duplicate')}
                            >
                                <CopyOutlined style={{ fontSize: '14px' }} />
                                <span>Duplicate</span>
                                <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>⌘D</span>
                            </div>
                        </>
                    )}
                    
                    {/* Delete - only show when nodes are selected */}
                    {(selectedNodes.length > 0 || selectedNode) && (
                        <>
                            <div style={{ borderTop: '1px solid #f0f0f0', margin: '4px 0' }} />
                            <div
                                style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    color: '#ff4d4f'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#fff1f0'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                onClick={() => handleContextMenuAction('delete')}
                            >
                                <DeleteOutlined style={{ fontSize: '14px' }} />
                                <span>Delete</span>
                                <span style={{ marginLeft: 'auto', color: '#8c8c8c', fontSize: '12px' }}>Del</span>
                            </div>
                        </>
                    )}
                    
                    {/* Show message if no actions available */}
                    {!selectedNodes.length && !selectedNode && copiedNodes.length === 0 && (
                        <div
                            style={{
                                padding: '8px 16px',
                                color: '#8c8c8c',
                                fontStyle: 'italic'
                            }}
                        >
                            No actions available
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}, (prevProps, nextProps) => {
    // Custom comparison để tránh re-render không cần thiết
    // Return true = props giống nhau = KHÔNG re-render
    // Return false = props khác nhau = CÓ re-render
    
    const isSame = prevProps.ruleInfo?.name === nextProps.ruleInfo?.name &&
           prevProps.isExecuting === nextProps.isExecuting &&
           prevProps.showExecutionPanel === nextProps.showExecutionPanel &&
           prevProps.executionResult === nextProps.executionResult &&
           prevProps.executingNodeId === nextProps.executingNodeId &&
           prevProps.errorNodeId === nextProps.errorNodeId &&
           prevProps.debugNodeId === nextProps.debugNodeId &&
           prevProps.debugPausedAtNodeId === nextProps.debugPausedAtNodeId &&
           prevProps.isDebugMode === nextProps.isDebugMode &&
           JSON.stringify(prevProps.completedNodeIds) === JSON.stringify(nextProps.completedNodeIds) &&
           JSON.stringify(prevProps.animatingEdgeIds) === JSON.stringify(nextProps.animatingEdgeIds) &&
           JSON.stringify(prevProps.currentRule) === JSON.stringify(nextProps.currentRule);
    
    return isSame;
});

export default FlowCanvas;